import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAccessibility } from '@/hooks/useAccessibility';
import { cn } from '@/lib/utils';
import { getChartTheme } from '../../theme/chartTheme';
import { DashPanel } from '../DashPanel';
import { DataCardHeader } from '../CardExportButton';

function useChartColors() {
  const { isDarkMode } = useAccessibility();
  return getChartTheme(isDarkMode);
}

const CHART_HEIGHT = 200;

export function LineTrendChart({
  title,
  data,
  dataKey,
  xKey,
  onExport,
}: {
  title: string;
  data: Record<string, string | number>[];
  dataKey: string;
  xKey: string;
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const w = Math.max(240, data.length * 56);

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="w-full overflow-x-auto scroll-styled">
        <LineChart width={w} height={CHART_HEIGHT} data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={xKey} tick={{ fill: colors.axis, fontSize: 11 }} axisLine={{ stroke: colors.grid }} tickLine={false} />
          <YAxis tick={{ fill: colors.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
          <Tooltip contentStyle={{ background: colors.tooltipBg, border: `1px solid ${colors.tooltipBorder}`, borderRadius: 4, fontSize: 12, color: colors.tooltipText, boxShadow: 'none' }} />
          <Line type="monotone" dataKey={dataKey} stroke={colors.primary} strokeWidth={2} dot={{ r: 3, fill: colors.primary, strokeWidth: 0 }} activeDot={{ r: 4 }} isAnimationActive={false} />
        </LineChart>
      </div>
    </DashPanel>
  );
}

export function ColumnChart({
  title,
  data,
  nameKey,
  valueKey,
  onExport,
}: {
  title: string;
  data: Record<string, string | number>[];
  nameKey: string;
  valueKey: string;
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const w = Math.max(240, data.length * 72);

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="w-full overflow-x-auto scroll-styled">
        <BarChart width={w} height={CHART_HEIGHT} data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={nameKey} tick={{ fill: colors.axis, fontSize: 11 }} axisLine={{ stroke: colors.grid }} tickLine={false} />
          <YAxis tick={{ fill: colors.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
          <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ background: colors.tooltipBg, border: `1px solid ${colors.tooltipBorder}`, borderRadius: 4, fontSize: 12, color: colors.tooltipText, boxShadow: 'none' }} />
          <Bar dataKey={valueKey} fill={colors.primary} radius={[2, 2, 0, 0]} maxBarSize={40} isAnimationActive={false} />
        </BarChart>
      </div>
    </DashPanel>
  );
}

export function BarChartHorizontal({
  title,
  data,
  nameKey,
  valueKey,
  onExport,
}: {
  title: string;
  data: Record<string, string | number>[];
  nameKey: string;
  valueKey: string;
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const h = Math.max(CHART_HEIGHT, data.length * 32);

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      {/* Usa height fixo em pixel no ResponsiveContainer — elimina loop do ResizeObserver */}
      <div className={cn('w-full', h > CHART_HEIGHT && 'overflow-y-auto scroll-styled')} style={{ maxHeight: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" hide />
            <YAxis type="category" dataKey={nameKey} width={88} tick={{ fill: colors.axis, fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: colors.cursor }} contentStyle={{ background: colors.tooltipBg, border: `1px solid ${colors.tooltipBorder}`, borderRadius: 4, fontSize: 12, color: colors.tooltipText, boxShadow: 'none' }} />
            <Bar dataKey={valueKey} fill={colors.primary} radius={[0, 2, 2, 0]} barSize={16} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </DashPanel>
  );
}

export function MultiLineChart({
  title,
  data,
  xKey,
  lines,
  onExport,
}: {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  lines: readonly { key: string; color: string; label: string }[];
  onExport?: () => void;
}) {
  const colors = useChartColors();
  const w = Math.max(320, data.length * 88);

  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <div className="w-full overflow-x-auto scroll-styled">
        <LineChart width={w} height={CHART_HEIGHT} data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={xKey} tick={{ fill: colors.axis, fontSize: 11 }} axisLine={{ stroke: colors.grid }} tickLine={false} />
          <YAxis tick={{ fill: colors.axis, fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
          <Tooltip contentStyle={{ background: colors.tooltipBg, border: `1px solid ${colors.tooltipBorder}`, borderRadius: 4, fontSize: 12, color: colors.tooltipText, boxShadow: 'none' }} />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
          {lines.map((line) => (
            <Line key={line.key} type="monotone" dataKey={line.key} name={line.label} stroke={line.color} strokeWidth={2} dot={{ r: 3, fill: line.color, strokeWidth: 0 }} activeDot={{ r: 4 }} isAnimationActive={false} />
          ))}
        </LineChart>
      </div>
    </DashPanel>
  );
}

export function SimpleTable({
  title,
  rows,
  onExport,
}: {
  title: string;
  rows: { label: string; value: string | number }[];
  onExport?: () => void;
}) {
  return (
    <DashPanel padding="sm">
      <DataCardHeader title={title} onExport={onExport} />
      <table className="w-full text-sm">
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-t border-border first:border-0">
              <td className="py-2.5 text-muted-foreground">{row.label}</td>
              <td className="py-2.5 text-right font-medium text-foreground tabular-nums">
                {typeof row.value === 'number' ? row.value.toLocaleString('pt-BR') : row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </DashPanel>
  );
}
